import { formatFixed, type FixedOptions } from '../number/fixed';

/**
 * Percentages and basis points, one way across the app. A served rate
 * arrives in one of three scales, and the caller names which:
 * - `percent`: already a percentage (4.2 is 4.2%);
 * - `fraction`: a share of one (0.042 is 4.2%), scaled `value * 100`;
 * - `bps`: basis points (420 is 4.2%), scaled `value / 100`.
 *
 * The scaling is that exact arithmetic, so a figure moved here prints the
 * same digits it printed inline.
 */
type PercentScale = 'percent' | 'fraction' | 'bps';

interface PercentOptions extends Omit<FixedOptions, 'digits'> {
  /** The scale the value is served in. Default `percent`. */
  from?: PercentScale;
  /**
   * Places after the point. On top of `formatFixed`'s, `carried` is for
   * basis points: as many places as the setting carries, none for a whole
   * percent, one for a tenth, two otherwise (4725 reads "47.25", 4730
   * "47.3", 4700 "47").
   */
  digits?: FixedOptions['digits'] | 'carried';
}

function toPercent(value: number, from: PercentScale): number {
  if (from === 'fraction') return value * 100;
  if (from === 'bps') return value / 100;
  return value;
}

function carriedPlaces(bps: number): number {
  return bps % 100 === 0 ? 0 : bps % 10 === 0 ? 1 : 2;
}

/**
 * A rate as a percentage: `formatPercent(0.042, { from: 'fraction', digits: 1 })`
 * reads "4.2%". Signs, places, trimming and the unit follow `formatFixed`; the
 * unit defaults to "%", and " pp" prints a difference in percentage points.
 * Null reads as the em dash (or `empty`), never as 0%.
 */
export function formatPercent(value: number, options?: PercentOptions): string;

export function formatPercent(
  value: number | null | undefined,
  options?: PercentOptions
): string;

export function formatPercent(
  value: number | null | undefined,
  { from = 'percent', digits, unit = '%', ...rest }: PercentOptions = {}
): string {
  if (value == null) return formatFixed(value, rest);
  return formatFixed(toPercent(value, from), {
    ...rest,
    unit,
    digits: digits === 'carried' ? carriedPlaces(value) : digits
  });
}
